/**
 * Parse a date string to ISO format
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const formats = [
    /^(\d{2})\/(\d{2})\/(\d{4})$/,
    /^(\d{4})-(\d{2})-(\d{2})$/,
    /^(\d{2})-(\d{2})-(\d{4})$/,
  ];
  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      if (format === formats[0]) return `${match[3]}-${match[2]}-${match[1]}`;
      if (format === formats[1]) return dateStr;
      if (format === formats[2]) return `${match[3]}-${match[2]}-${match[1]}`;
    }
  }
  return null;
};

export const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('fr-FR');
  } catch { return dateStr; }
};

export const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.,-]/g, '').replace(',', '.')) : value;
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
};

export const formatPercentage = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.,-]/g, '').replace(',', '.')) : value;
  if (isNaN(num)) return value;
  return `${num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
};

export const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('0')) {
    return digits.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  if (digits.length > 10) {
    return `+${digits.slice(0, -9)} ${digits.slice(-9).replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}`;
  }
  return value;
};

export const formatNumeric = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '';
  const { decimalPlaces = 2, useThousandsSeparator = true } = options;
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.,-]/g, '').replace(',', '.')) : value;
  if (isNaN(num)) return value;
  return num.toLocaleString('fr-FR', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces, useGrouping: useThousandsSeparator });
};

/**
 * Get column key from column name (converts to snake_case for extracted_data access)
 */
export const getColumnKey = (colName) => {
  return colName.toLowerCase().replace(/\s+/g, '_').replace(/'/g, '').replace(/-/g, '_').replace(/\./g, '_');
};

/**
 * Resolve the actual key used in extracted_data for a column.
 * Returns the key that currently holds a non-empty value, or falls back to getColumnKey.
 */
export const resolveActualKey = (extracted, colName) => {
  const key = getColumnKey(colName);
  // Check all candidate keys and return the one that has data
  const candidates = [
    key,
    colName,
    colName.toUpperCase(),
    colName.toLowerCase(),
    colName.toLowerCase().replace(/\s+/g, '.'),
    colName.toUpperCase().replace(/\s+/g, '_'),
  ];
  for (const k of candidates) {
    if (k in extracted && extracted[k] !== undefined && extracted[k] !== null && extracted[k] !== '') {
      return k;
    }
  }
  // If no existing key found, return the first key that exists (even if empty), or the default key
  for (const k of candidates) {
    if (k in extracted) return k;
  }
  return key;
};

/**
 * Update a value in extracted_data for ALL key variants of a column name.
 * This ensures that duplicate keys (e.g. "ORDER STATUS" and "order_status") stay in sync.
 */
export const updateAllKeyVariants = (extracted, colName, value) => {
  const key = getColumnKey(colName);
  const candidates = [
    key,
    colName,
    colName.toUpperCase(),
    colName.toLowerCase(),
    colName.toLowerCase().replace(/\s+/g, '.'),
    colName.toUpperCase().replace(/\s+/g, '_'),
  ];
  const updated = { ...extracted };
  let primarySet = false;
  for (const k of candidates) {
    if (k in updated) {
      updated[k] = value;
      primarySet = true;
    }
  }
  if (!primarySet) {
    updated[resolveActualKey(extracted, colName)] = value;
  }
  return updated;
};

/**
 * Format date to French format (JJ/MM/AAAA)
 */
export const formatDateFrench = (dateStr) => {
  if (!dateStr || dateStr === '-') return dateStr;
  const str = String(dateStr).trim();
  if (!str) return '';
  let day, month, year, match;

  match = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (match) {
    const first = parseInt(match[1], 10);
    const second = parseInt(match[2], 10);
    year = match[3];
    if (first > 12) { day = String(first).padStart(2, '0'); month = String(second).padStart(2, '0'); }
    else if (second > 12) { month = String(first).padStart(2, '0'); day = String(second).padStart(2, '0'); }
    else { day = String(first).padStart(2, '0'); month = String(second).padStart(2, '0'); }
    return `${day}/${month}/${year}`;
  }

  match = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (match) return `${match[1].padStart(2, '0')}/${match[2].padStart(2, '0')}/${match[3]}`;

  match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) return `${match[3].padStart(2, '0')}/${match[2].padStart(2, '0')}/${match[1]}`;

  const monthNames = {
    'janvier':'01','january':'01','januar':'01','jan':'01',
    'février':'02','february':'02','februar':'02','feb':'02','fév':'02',
    'mars':'03','march':'03','märz':'03','mar':'03',
    'avril':'04','april':'04','apr':'04','avr':'04',
    'mai':'05','may':'05',
    'juin':'06','june':'06','juni':'06','jun':'06',
    'juillet':'07','july':'07','juli':'07','jul':'07',
    'août':'08','august':'08','aug':'08','aou':'08',
    'septembre':'09','september':'09','sep':'09','sept':'09',
    'octobre':'10','october':'10','oktober':'10','oct':'10',
    'novembre':'11','november':'11','nov':'11',
    'décembre':'12','december':'12','dezember':'12','dec':'12','déc':'12'
  };

  match = str.match(/^(\d{1,2})\.?\s*([a-zéûô]+)\s+(\d{4})$/i);
  if (match) {
    day = match[1].padStart(2, '0');
    month = monthNames[match[2].toLowerCase()];
    year = match[3];
    if (month) return `${day}/${month}/${year}`;
  }

  match = str.match(/^([a-zéûô]+)\s+(\d{1,2}),?\s+(\d{4})$/i);
  if (match) {
    month = monthNames[match[1].toLowerCase()];
    day = match[2].padStart(2, '0');
    year = match[3];
    if (month) return `${day}/${month}/${year}`;
  }

  return str;
};

/**
 * Get cell value from order with flexible key matching
 */
export const getCellValue = (order, column) => {
  const extracted = order.extracted_data || {};
  const key = getColumnKey(column.name);

  let value = extracted[key];
  if (value === undefined || value === null || value === '') value = extracted[column.name];
  if (value === undefined || value === null || value === '') value = extracted[column.name.toUpperCase()];
  if (value === undefined || value === null || value === '') value = extracted[column.name.toLowerCase()];
  if (value === undefined || value === null || value === '') {
    value = extracted[column.name.toLowerCase().replace(/\s+/g, '.')];
  }
  if (value === undefined || value === null || value === '') {
    value = extracted[column.name.toUpperCase().replace(/\s+/g, '_')];
  }

  if (value === undefined || value === null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  if (column.type === 'date') return formatDateFrench(String(value));
  return String(value);
};

/**
 * Format cell value based on column type
 */
export const formatCellValue = (value, columnType, columnConfig = {}) => {
  if (!value && value !== 0 && value !== false) return null;
  switch (columnType) {
    case 'amount': {
      const num = parseFloat(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
      if (isNaN(num)) return value;
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
    }
    case 'numerique': {
      const numVal = parseFloat(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
      if (isNaN(numVal)) return value;
      const dp = columnConfig.decimal_places ?? 2;
      const useT = columnConfig.use_thousands_separator !== false;
      if (useT) return numVal.toLocaleString('fr-FR', { minimumFractionDigits: dp, maximumFractionDigits: dp });
      return numVal.toFixed(dp).replace('.', ',');
    }
    case 'percentage': {
      const pct = parseFloat(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
      if (isNaN(pct)) return value;
      return `${pct.toLocaleString('fr-FR')} %`;
    }
    case 'date': {
      if (!value) return value;
      const dateStr = String(value).trim();
      const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s|T|$)/);
      if (isoMatch) return `${isoMatch[3].padStart(2, '0')}/${isoMatch[2].padStart(2, '0')}/${isoMatch[1]}`;
      const germanMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (germanMatch) return `${germanMatch[1].padStart(2, '0')}/${germanMatch[2].padStart(2, '0')}/${germanMatch[3]}`;
      const frenchMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (frenchMatch) return `${frenchMatch[1].padStart(2, '0')}/${frenchMatch[2].padStart(2, '0')}/${frenchMatch[3]}`;
      return value;
    }
    case 'checkbox':
      return value === true || value === 'true' || value === '1' || value === 'oui' || value === 'Oui';
    case 'phone': {
      if (!value) return value;
      let phone = String(value).replace(/[^\d+]/g, '');
      if (phone.startsWith('0') && phone.length >= 10) phone = '+33' + phone.substring(1);
      if (!phone.startsWith('+') && phone.length === 9 && /^[67]/.test(phone)) phone = '+33' + phone;
      if (phone.startsWith('+33') && phone.length >= 12) {
        const digits = phone.substring(3);
        return '+33 ' + digits.replace(/(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
      }
      if (phone.startsWith('+')) {
        return phone.replace(/(\+\d{2,3})(\d{1,3})(\d{2,3})(\d{2,3})(\d{2,4})?/, '$1 $2 $3 $4 $5').trim();
      }
      return value;
    }
    default:
      return value;
  }
};
